"use client";

/**
 * The chrome layer. It sits exactly over the stage, reads WHERE from context,
 * and takes no geometry prop — a variant that could be told where to be is a
 * variant that can disagree with the stage it is meant to be decorating.
 *
 * It owns nothing. Composing a full call and a minimised one is choosing which
 * chrome to put inside a frame, never which stage to mount:
 *
 *   <Call.Frame><Call.Header><Call.Minimise /></Call.Header><Call.WaitingList /><Call.Controls /></Call.Frame>
 *   <Call.Frame><Call.WaitingChip /><Call.Expand /></Call.Frame>
 */
import { cn } from "@repo/ui/lib/utils";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useCall } from "./context";

export interface CallFrameProps {
  children: ReactNode;
  className?: string;
}

export function CallFrame({ children, className }: CallFrameProps) {
  const { geometry } = useCall();

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      data-testid="call-frame"
      onPointerDown={geometry.onPointerDown}
      className={cn(
        "fixed z-40 overflow-hidden",
        geometry.minimised
          ? cn(
              "aspect-video w-80 touch-none select-none rounded-2xl",
              geometry.dragging ? "cursor-grabbing" : "cursor-grab",
            )
          : "",
        className,
      )}
      style={geometry.stageStyle}
    >
      {children}
    </div>,
    document.body,
  );
}
